// Once a trip is shipped, load/unload disappear and the page becomes a
// read-only packing list — depart flips trip.status irreversibly.

import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Alert,
  Table,
  Modal,
} from 'react-bootstrap';

import {
  useTrip,
  useLoadItem,
  useUnloadItem,
  useDepartTrip,
  usePackingList,
} from '../../features/warehouse/useTrips.js';
import {
  tripStatusColor,
  extractErrorMessage,
  useFlashBanner,
} from '../../features/warehouse/warehouseUtils.js';
import LoadableItemsPanel from '../../components/warehouse/LoadableItemsPanel.js';

export default function TripDetailPage({ code, onBack, onOpenTruckView, onOpenPickRoute, onOpenCmr }) {
  const { data: trip, isLoading, error } = useTrip(code);
  const loadItem = useLoadItem(code);
  const unloadItem = useUnloadItem(code);
  const departTrip = useDepartTrip(code);

  const { banner, flash } = useFlashBanner();
  const [packingOpen, setPackingOpen] = useState(false);

  const handleLoad = (serial) => {
    loadItem.mutate(
      { serial },
      {
        onSuccess: () => flash('success', `${serial} loaded`),
        onError: (err) =>
          flash('danger', extractErrorMessage(err, 'Load failed'), { autoHide: false }),
      }
    );
  };

  const handleUnload = (serial) => {
    if (!window.confirm(`Unload ${serial}? It will go back to "to put away".`)) return;
    unloadItem.mutate(
      { serial },
      {
        onSuccess: () => flash('success', `${serial} unloaded`),
        onError: (err) =>
          flash('danger', extractErrorMessage(err, 'Unload failed'), { autoHide: false }),
      }
    );
  };

  const handleDepart = () => {
    if (!trip?.items?.length) {
      flash('warning', 'Cannot depart an empty trip.', { autoHide: false });
      return;
    }
    if (!window.confirm(
      `Depart trip ${trip.code} with ${trip.items.length} item(s)? This marks them as shipped and is irreversible.`
    )) return;
    departTrip.mutate(undefined, {
      onSuccess: () => {
        flash('success', `Trip ${trip.code} departed.`);
        setPackingOpen(true);
      },
      onError: (err) =>
        flash('danger', extractErrorMessage(err, 'Depart failed'), { autoHide: false }),
    });
  };

  if (isLoading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error || !trip) {
    return (
      <Container fluid className="py-3">
        <Button variant="link" onClick={onBack} className="p-0 mb-2">&larr; Back to trips</Button>
        <Alert variant="danger">
          {error?.message || `Trip ${code} not found.`}
        </Alert>
      </Container>
    );
  }

  const isClosed = trip.status === 'shipped' || trip.status === 'invoiced';
  const busy = loadItem.isPending || unloadItem.isPending || departTrip.isPending;

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <Row className="mb-3 align-items-center">
        <Col>
          <Button variant="link" onClick={onBack} className="p-0 mb-1">
            &larr; Back to trips
          </Button>
          <h4 className="mb-0">
            {trip.code}{' '}
            <Badge bg={tripStatusColor(trip.status)}>{trip.status}</Badge>
          </h4>
          <div className="text-muted small">
            {trip.plate && <>Plate <strong>{trip.plate}</strong> · </>}
            {trip.driver && <>Driver <strong>{trip.driver}</strong> · </>}
            {trip.customer && <>Customer <strong>{trip.customer}</strong></>}
          </div>
        </Col>
        <Col xs="auto">
          {onOpenPickRoute && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onOpenPickRoute(trip.code)}
              className="me-2"
            >
              Picking list
            </Button>
          )}
          {onOpenTruckView && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onOpenTruckView(trip.code)}
              disabled={!trip.items?.length}
              className="me-2"
            >
              Visualise load
            </Button>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPackingOpen(true)}
            className="me-2"
          >
            Packing list
          </Button>
          {onOpenCmr && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onOpenCmr(trip.code)}
              disabled={!trip.items?.length}
              className="me-2"
            >
              CMR
            </Button>
          )}
          {!isClosed && (
            <Button
              variant="success"
              size="sm"
              onClick={handleDepart}
              disabled={busy || !trip.items?.length}
            >
              Depart
            </Button>
          )}
        </Col>
      </Row>

      {banner && (
        <Alert variant={banner.variant} className="py-2 small">
          {banner.text}
        </Alert>
      )}

      <Row>
        {/* Left: items already on the trip */}
        <Col md={isClosed ? 12 : 8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>On this trip</strong>
              <div className="small text-muted">
                {trip.items?.length || 0} items ·{' '}
                {trip.totalLengthM || 0} m ·{' '}
                {trip.totalWeightKg || 0} kg
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {(!trip.items || trip.items.length === 0) && (
                <div className="text-center text-muted py-5">
                  No items loaded yet. Pick from the right panel.
                </div>
              )}
              {trip.items?.length > 0 && (
                <Table hover responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Serial</th>
                      <th>Type</th>
                      <th>Cable</th>
                      <th className="text-end">Length</th>
                      <th className="text-end">Weight</th>
                      {!isClosed && <th className="text-end">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {trip.items.map(item => (
                      <tr key={item._id}>
                        <td><strong>{item.serial}</strong></td>
                        <td className="small">{item.type}</td>
                        <td className="small">{item.cableName}</td>
                        <td className="text-end">{item.lengthMeters} m</td>
                        <td className="text-end">{item.weightKg || 0} kg</td>
                        {!isClosed && (
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={busy}
                              onClick={() => handleUnload(item.serial)}
                            >
                              ×
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right: picker (hidden once the trip is shipped) */}
        {!isClosed && (
          <Col md={4}>
            <LoadableItemsPanel onPick={handleLoad} busy={busy} />
          </Col>
        )}
      </Row>

      <PackingListModal
        show={packingOpen}
        onHide={() => setPackingOpen(false)}
        code={trip.code}
      />
    </Container>
  );
}

// ---------------------------------------------------------------------------

function PackingListModal({ show, onHide, code }) {
  const { data, isLoading, error } = usePackingList(show ? code : null);

  const handlePrint = () => window.print();

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Packing list — {code}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" />
          </div>
        )}
        {error && <Alert variant="danger">{error.message}</Alert>}
        {data && (
          <div>
            <div className="mb-3 small">
              <div><strong>Plate:</strong> {data.trip.plate || '—'}</div>
              <div><strong>Driver:</strong> {data.trip.driver || '—'}</div>
              <div><strong>Customer:</strong> {data.trip.customer || '—'}</div>
            </div>
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Serial</th>
                  <th>Type</th>
                  <th>Cable</th>
                  <th className="text-end">Length</th>
                  <th className="text-end">Weight</th>
                </tr>
              </thead>
              <tbody>
                {(data.lines || []).map((l, idx) => (
                  <tr key={l.serial}>
                    <td>{idx + 1}</td>
                    <td><strong>{l.serial}</strong></td>
                    <td>{l.type}</td>
                    <td>{l.cableName}</td>
                    <td className="text-end">{l.lengthMeters} m</td>
                    <td className="text-end">{l.weightKg || 0} kg</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="text-end"><strong>Total</strong></td>
                  <td className="text-end"><strong>{data.totalLengthM || 0} m</strong></td>
                  <td className="text-end"><strong>{data.totalWeightKg || 0} kg</strong></td>
                </tr>
              </tfoot>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handlePrint}>
          Print
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
